import { useMemo, useRef, useState } from "react";
import {
  Trophy,
  Target,
  Award,
  Medal,
  type LucideIcon,
  X,
  ArrowUpRight,
} from "lucide-react";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  type PanInfo,
} from "framer-motion";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { AboutReveal, AboutStagger, AboutStaggerItem } from "./about-reveal";
import { cn } from "@/lib/utils";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { achievementsCopy } from "@/lib/aboutPageCopy";

const SIH2025 = "/About/sih2025.png";
const SIH2024 = "/About/sih2024.png";
const hackathon2 = "/About/hackathon2.0.png";
const techx = "/About/techx.png";
const bwi = "/About/bwi.png";

const achievementMeta: {
  image: string;
  fallbackIcon: LucideIcon;
  year: string;
}[] = [
  { image: hackathon2, fallbackIcon: Trophy, year: "2026" },
  { image: SIH2025, fallbackIcon: Trophy, year: "2025" },
  { image: techx, fallbackIcon: Medal, year: "2025" },
  { image: bwi, fallbackIcon: Award, year: "2025" },
  { image: SIH2024, fallbackIcon: Target, year: "2024" },
];

export type Achievement = {
  title: string;
  caption: string;
  image: string;
  fallbackIcon: LucideIcon;
  year: string;
  organizer: string;
  description: string;
  highlights: string[];
};

function AchievementCard({
  item,
  viewDetailsLabel,
  onOpen,
  className,
}: {
  item: Achievement;
  viewDetailsLabel: string;
  onOpen: (item: Achievement) => void;
  className?: string;
}) {
  return (
    <article
      className={cn(
        "group relative isolate aspect-[3/4] w-full cursor-pointer overflow-hidden rounded-[1.5rem] bg-muted shadow-[0_20px_50px_-24px_rgba(0,0,0,0.35)] ring-1 ring-black/5 dark:ring-white/10",
        className
      )}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <img
        src={item.image}
        alt={item.title}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        draggable={false}
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,0.12),_transparent_55%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      />

      <div className="absolute inset-x-0 bottom-0 flex flex-col p-5">
        <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-white/65">
          {item.caption}
        </p>
        <h3 className="text-balance text-[15px] font-semibold leading-snug tracking-tight text-white md:text-base">
          {item.title}
        </h3>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(item);
          }}
          className="pointer-events-auto relative z-10 mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-white/95 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-950 shadow-sm backdrop-blur-sm transition-all duration-300 group-hover:scale-[1.02] group-hover:bg-white"
        >
          {viewDetailsLabel}
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
        </button>
      </div>
    </article>
  );
}

function MobileAchievementStack({
  achievements,
  viewDetailsLabel,
  onOpen,
}: {
  achievements: Achievement[];
  viewDetailsLabel: string;
  onOpen: (item: Achievement) => void;
}) {
  const [index, setIndex] = useState(0);
  const [exitY, setExitY] = useState(-180);
  const dragged = useRef(false);
  const reduceMotion = useReducedMotion();
  const total = achievements.length;
  const active = achievements[index];
  const isFirst = index === 0;
  const isLast = index === total - 1;

  const scrollPage = (direction: "up" | "down") => {
    const amount = Math.min(window.innerHeight * 0.55, 420);
    window.scrollBy({
      top: direction === "down" ? amount : -amount,
      behavior: "smooth",
    });
  };

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const { offset, velocity } = info;
    const swipeUp = offset.y < -72 || velocity.y < -450;
    const swipeDown = offset.y > 72 || velocity.y > 450;
    const movedEnough = Math.abs(offset.y) > 18 || Math.abs(velocity.y) > 200;

    if (movedEnough) dragged.current = true;

    if (swipeUp) {
      if (isLast) {
        scrollPage("down");
        return;
      }
      setExitY(-180);
      setIndex((i) => i + 1);
      return;
    }

    if (swipeDown) {
      if (isFirst) {
        scrollPage("up");
        return;
      }
      setExitY(180);
      setIndex((i) => i - 1);
    }
  };

  const behind = [1, 2]
    .map((offset) => {
      const nextIndex = index + offset;
      if (nextIndex >= total) return null;
      return { offset, item: achievements[nextIndex] };
    })
    .filter((entry): entry is { offset: number; item: Achievement } => !!entry);

  return (
    <div className="md:hidden">
      <div className="relative mx-auto h-[min(72vh,560px)] w-full max-w-[400px] select-none px-1">
        {behind.map(({ item, offset }) => (
          <motion.div
            key={`behind-${item.title}-${offset}`}
            className="absolute inset-x-1 inset-y-0"
            style={{ zIndex: 10 - offset }}
            initial={false}
            animate={{
              scale: 1 - offset * 0.045,
              y: offset * 14,
              opacity: 1 - offset * 0.12,
            }}
            transition={{ type: "spring", stiffness: 340, damping: 30 }}
          >
            <AchievementCard
              item={item}
              viewDetailsLabel={viewDetailsLabel}
              onOpen={onOpen}
              className="pointer-events-none h-full shadow-[0_24px_50px_-20px_rgba(0,0,0,0.4)]"
            />
          </motion.div>
        ))}

        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={active.title}
            className="absolute inset-x-1 inset-y-0 z-20 cursor-grab active:cursor-grabbing"
            drag={reduceMotion ? false : "y"}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.7}
            onDragStart={() => {
              dragged.current = false;
            }}
            onDrag={(_, info) => {
              if (Math.abs(info.offset.y) > 10) dragged.current = true;
            }}
            onDragEnd={handleDragEnd}
            onClick={() => {
              if (!dragged.current) onOpen(active);
            }}
            initial={{ opacity: 0, y: 48, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: exitY,
              scale: 0.94,
              transition: { duration: 0.26, ease: [0.23, 1, 0.32, 1] },
            }}
            transition={{ type: "spring", stiffness: 400, damping: 34 }}
            whileDrag={{ scale: 1.015 }}
          >
            <AchievementCard
              item={active}
              viewDetailsLabel={viewDetailsLabel}
              onOpen={onOpen}
              className="h-full shadow-[0_28px_60px_-18px_rgba(0,0,0,0.45)] transition-none hover:translate-y-0 hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.45)] [&_*]:pointer-events-none [&_button]:pointer-events-auto"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-5 flex items-center justify-center gap-2">
        {achievements.map((item, i) => (
          <button
            key={item.title}
            type="button"
            aria-label={`Go to ${item.title}`}
            onClick={() => setIndex(i)}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              i === index
                ? "w-6 bg-foreground"
                : "w-1.5 bg-muted-foreground/35 hover:bg-muted-foreground/55"
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function Achievements() {
  const lang = useSiteLanguage();
  const t = achievementsCopy[lang];
  const [selected, setSelected] = useState<Achievement | null>(null);
  const [open, setOpen] = useState(false);

  const achievements = useMemo<Achievement[]>(
    () =>
      achievementMeta.map((meta, index) => ({
        ...meta,
        ...t.items[index],
      })),
    [t]
  );

  const openAchievement = (item: Achievement) => {
    setSelected(item);
    setOpen(true);
  };

  return (
    <section
      id="achievements"
      className="scroll-mt-24 bg-background pb-16 md:pb-24 lg:pb-28"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <AboutReveal className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <span className="mb-4 block text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            {t.eyebrow}
          </span>
          <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground md:text-3xl lg:text-[2.15rem]">
            {t.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-[15px]">
            {t.subtitle}
          </p>
        </AboutReveal>

        <MobileAchievementStack
          achievements={achievements}
          viewDetailsLabel={t.viewDetails}
          onOpen={openAchievement}
        />

        <AboutStagger className="relative z-10 hidden gap-5 sm:grid-cols-2 md:grid lg:grid-cols-5 lg:gap-4 xl:gap-5">
          {achievements.map((item) => (
            <AboutStaggerItem key={item.title} className="relative z-10">
              <AchievementCard
                item={item}
                viewDetailsLabel={t.viewDetails}
                onOpen={openAchievement}
                className="relative z-10 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-20px_rgba(0,0,0,0.45)]"
              />
            </AboutStaggerItem>
          ))}
        </AboutStagger>
      </div>

      <Drawer
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setTimeout(() => setSelected(null), 280);
        }}
      >
        <DrawerContent className="max-h-[92vh] border-border bg-background">
          <DrawerTitle className="sr-only">
            {selected?.title ?? t.detailsAria}
          </DrawerTitle>
          {!selected && (
            <DrawerDescription className="sr-only">
              {t.loadingAria}
            </DrawerDescription>
          )}
          {selected && (
            <div className="relative mx-auto w-full max-w-5xl overflow-y-auto px-4 pb-8 pt-2 sm:px-6 md:px-8">
              <DrawerClose
                className="absolute right-3 top-2 z-30 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm transition-colors hover:bg-muted md:right-4 md:top-3"
                aria-label={t.close}
              >
                <X className="h-4 w-4" strokeWidth={2.25} />
              </DrawerClose>

              <DrawerHeader className="relative px-0 text-left">
                <div className="grid gap-6 pt-1 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] md:gap-8 md:pt-2 lg:gap-10">
                  <div className="relative overflow-hidden rounded-[1.35rem] bg-muted ring-1 ring-border md:rounded-[1.5rem]">
                    <div className="flex max-h-[min(58vh,480px)] items-center justify-center p-2 sm:p-3 md:max-h-[min(70vh,520px)] md:min-h-[360px]">
                      <img
                        src={selected.image}
                        alt={selected.title}
                        className="max-h-[min(54vh,460px)] w-full object-contain md:max-h-[min(66vh,500px)]"
                      />
                    </div>
                  </div>

                  <div className="flex min-w-0 flex-col md:py-2">
                    <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      {selected.organizer} · {selected.year}
                    </p>
                    <p className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-[1.75rem] lg:text-3xl">
                      {selected.title}
                    </p>

                    <DrawerDescription className="mt-4 text-left text-[15px] leading-relaxed text-muted-foreground md:text-base">
                      {selected.description}
                    </DrawerDescription>

                    <div className="mt-7 space-y-4 border-t border-border pt-7">
                      <h4 className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                        {t.highlights}
                      </h4>
                      <ul className="space-y-3.5">
                        {selected.highlights.map((point) => (
                          <li
                            key={point}
                            className="flex items-start gap-3 text-sm leading-relaxed text-foreground md:text-[15px]"
                          >
                            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500 dark:bg-sky-400" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </DrawerHeader>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </section>
  );
}
